import { useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TeamMemberResultStatus } from '@gg/shared';
import { Button, Notice, StickyFooter, TextField } from '@/components/ui';
import { ApiRequestError } from '@/lib/api';
import { clearInviteDraft, readInviteDraft, type InviteDraftKey } from '@/lib/inviteDraft';
import { useAddTeamMembers } from '@/lib/queries';
import { sentIfAllFilledSent, splitInviteFields } from '@/components/invitations/sendable';

type FormValues = { emails: { value: string }[] };

/** Error codes of POST /me/teams/:id/members that have their own message. */
const KNOWN_ERRORS = ['not_captain', 'team_cancelled', 'unauthorized'];
/** Addresses per request (`addTeamMembersInput`). */
const MAX_BATCH = 20;

/**
 * Teammate email fields of a captain (P-20, PRD §4.7 step 2): one per seat left, right after
 * payment (screen 04) and later from the profile. Same rules as the invitation fields: one
 * valid address is enough, refused addresses show their reason and stay editable, sent ones
 * are locked. When every filled address was sent, the captain goes to the profile.
 *
 * `maxFields` is the number of seats left (null = unlimited): while fields are fewer, an
 * "add a teammate" button appends one (reviewed 2026-10-28: no fixed three fields).
 *
 * `ready` is false while the confirmation screen waits for the session; `blocked` replaces
 * the submit. With `onHold` (no session to send with), the submit keeps the addresses instead.
 */
export function TeammateForm({
  teamId,
  fields: fieldCount,
  maxFields = fieldCount,
  ready = true,
  blocked,
  waitingNote,
  secondaryAction,
  onSent,
  draftKey,
  onHold,
}: {
  teamId: string;
  fields: number;
  maxFields?: number | null;
  ready?: boolean;
  blocked?: ReactNode;
  waitingNote?: ReactNode;
  secondaryAction?: ReactNode;
  /** Called instead of leaving for the profile when every filled address was sent. */
  onSent?: (count: number) => void;
  /** Pre-fills the fields with addresses kept from the confirmation screen; cleared once sent. */
  draftKey?: InviteDraftKey;
  /** No session yet (record 0027, R1): the submit hands over the valid addresses, sends nothing. */
  onHold?: (emails: string[]) => void;
}) {
  const { t } = useTranslation();
  const send = useAddTeamMembers(teamId);
  const navigate = useNavigate();
  const [results, setResults] = useState<Record<string, TeamMemberResultStatus>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [draft] = useState(() =>
    readInviteDraft(draftKey).slice(0, maxFields === null ? MAX_BATCH : Math.max(maxFields, 0)),
  );
  const form = useForm<FormValues>({
    defaultValues: {
      emails: Array.from({ length: Math.max(fieldCount, draft.length) }, (_, i) => ({
        value: draft[i] ?? '',
      })),
    },
  });
  const { fields, append } = useFieldArray({ control: form.control, name: 'emails' });
  const canAddField =
    (maxFields === null || fields.length < maxFields) && fields.length < MAX_BATCH;
  const values = form.watch('emails');
  const withValues = (emails: { value: string }[]) =>
    fields.map((f, i) => ({ id: f.id, value: emails[i]?.value ?? '' }));
  const canSubmit = splitInviteFields(withValues(values), results).send.length > 0;

  const onSubmit = form.handleSubmit(async ({ emails }) => {
    setFormError(null);
    const { send: pending, invalid } = splitInviteFields(withValues(emails), results);
    const flagged = Object.fromEntries(invalid.map((id) => [id, 'invalid' as const]));
    if (invalid.length > 0) setResults((prev) => ({ ...prev, ...flagged }));
    if (pending.length === 0) return;
    if (onHold) return onHold(pending.map((p) => p.value));
    try {
      const out = await send.mutateAsync({ emails: pending.map((p) => p.value) });
      clearInviteDraft(draftKey);
      const next: typeof results = { ...results, ...flagged };
      out.results.forEach((r, i) => {
        const field = pending[i];
        if (field) next[field.id] = r.status;
      });
      setResults(next);
      const allSent = sentIfAllFilledSent(withValues(emails), next);
      if (allSent !== null) {
        if (onSent) onSent(allSent);
        else navigate('/me', { state: { teammatesSent: allSent } });
      }
    } catch (err) {
      const code = err instanceof ApiRequestError ? err.code : '';
      setFormError(KNOWN_ERRORS.includes(code) ? t(`team.error.${code}`) : t('error.generic'));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col gap-2">
        {fields.map((field, i) => {
          const status = results[field.id];
          const n = i + 1;
          return (
            <TextField
              key={field.id}
              type="email"
              inputMode="email"
              autoComplete="off"
              aria-label={t('team.fieldLabel', { n })}
              placeholder={t('team.fieldPlaceholder', { n })}
              readOnly={status === 'sent'}
              hint={status === 'sent' ? t('team.result.sent') : undefined}
              hintTone="success"
              error={status && status !== 'sent' ? t(`team.result.${status}`) : undefined}
              {...form.register(`emails.${i}.value`, {
                // Editing a refused address clears its reason.
                onChange: () => {
                  if (!status || status === 'sent') return;
                  setResults((prev) => {
                    const next = { ...prev };
                    delete next[field.id];
                    return next;
                  });
                },
              })}
            />
          );
        })}
        {canAddField && (
          <Button
            type="button"
            variant="ghost"
            size="md"
            className="self-start w-auto!"
            onClick={() => append({ value: '' })}
          >
            {t('team.addField')}
          </Button>
        )}
      </div>

      <Notice>{t('team.notice')}</Notice>

      <StickyFooter>
        {formError && (
          <p role="alert" className="m-0 text-center text-helper text-danger">
            {formError}
          </p>
        )}
        {blocked ?? (
          <>
            <Button
              type="submit"
              loading={send.isPending}
              disabled={(!ready && !onHold) || !canSubmit}
            >
              {t('team.send')}
            </Button>
            {!ready && !onHold && waitingNote && (
              <p role="status" className="m-0 text-center text-helper text-ink-muted">
                {waitingNote}
              </p>
            )}
          </>
        )}
        {secondaryAction}
      </StickyFooter>
    </form>
  );
}
